import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Section } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import { Search as SearchIcon, Check, X, Loader2, ShoppingCart } from "lucide-react";

const POPULAR_TLDS = ["com", "net", "org", "io", "co", "dev", "app", "ai", "xyz", "tech"];

const DomainSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [tlds, setTlds] = useState(["com", "net", "org", "io", "co"]);
  const [bulkMode, setBulkMode] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleTld = (t) => {
    setTlds((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  };

  const run = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResults([]);
    try {
      let domains = [];
      if (bulkMode) {
        domains = query
          .split(/[\s,\n]+/)
          .map((d) => d.trim().toLowerCase())
          .filter(Boolean);
      } else {
        const root = query.trim().toLowerCase().replace(/\..+$/, "");
        domains = tlds.map((t) => `${root}.${t}`);
      }
      domains = domains.slice(0, 50);
      const { data } = await api.post("/domains/check", { domains });
      setResults(data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Step 01"
        title="Domain Search"
        description="Check availability of one root across multiple TLDs, or paste a bulk list."
      />
      <Section>
        <Card className="rounded-sm shadow-none border-[var(--border)]">
          <CardContent className="p-6 space-y-4">
            <div className="flex gap-2 items-center">
              <button
                data-testid="mode-single"
                onClick={() => setBulkMode(false)}
                className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-sm border ${
                  !bulkMode ? "brand-bg text-white border-transparent" : "border-[var(--border)] text-[var(--text-secondary)]"
                }`}
              >
                Multi-TLD
              </button>
              <button
                data-testid="mode-bulk"
                onClick={() => setBulkMode(true)}
                className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-sm border ${
                  bulkMode ? "brand-bg text-white border-transparent" : "border-[var(--border)] text-[var(--text-secondary)]"
                }`}
              >
                Bulk list
              </button>
            </div>

            {bulkMode ? (
              <textarea
                data-testid="bulk-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="example.com&#10;another.net&#10;test.io"
                className="w-full h-40 p-3 mono text-sm border border-[var(--border)] rounded-sm focus:outline-none focus:border-[var(--brand)]"
              />
            ) : (
              <>
                <div className="flex gap-2">
                  <Input
                    data-testid="search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="mybrand"
                    className="mono rounded-sm"
                    onKeyDown={(e) => e.key === "Enter" && run()}
                  />
                  <Button
                    data-testid="search-btn"
                    onClick={run}
                    disabled={loading}
                    className="brand-bg hover:brand-bg text-white rounded-sm min-w-[140px]"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4 mr-2" />}
                    {loading ? "Checking..." : "Check"}
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {POPULAR_TLDS.map((t) => (
                    <button
                      key={t}
                      data-testid={`tld-${t}`}
                      onClick={() => toggleTld(t)}
                      className={`mono text-xs px-3 py-1 border rounded-sm transition-colors ${
                        tlds.includes(t)
                          ? "border-[var(--brand)] text-[var(--brand)] bg-white"
                          : "border-[var(--border)] text-[var(--text-secondary)]"
                      }`}
                    >
                      .{t}
                    </button>
                  ))}
                </div>
              </>
            )}

            {bulkMode && (
              <Button
                data-testid="bulk-check-btn"
                onClick={run}
                disabled={loading}
                className="brand-bg hover:brand-bg text-white rounded-sm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <SearchIcon className="w-4 h-4 mr-2" />}
                Check bulk
              </Button>
            )}
          </CardContent>
        </Card>

        {results.length > 0 && (
          <div className="mt-6" data-testid="results-list">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-3">
              {results.length} results
            </div>
            <div className="border border-[var(--border)] rounded-sm bg-white divide-y divide-[var(--border)]">
              {results.map((r, i) => {
                const available = r.Available === "true" || r.Available === true;
                const premium = r.IsPremiumName === "true" || r.IsPremiumName === true;
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between px-4 py-3 dense-row"
                    data-testid={`result-${r.Domain}`}
                  >
                    <div className="flex items-center gap-3">
                      {available ? (
                        <Check className="w-4 h-4 text-[var(--success)]" />
                      ) : (
                        <X className="w-4 h-4 text-[var(--text-disabled)]" />
                      )}
                      <span className="mono text-sm">{r.Domain}</span>
                      {premium && (
                        <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-wider">
                          Premium
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`text-xs uppercase tracking-wider mono ${
                          available ? "text-[var(--success)]" : "text-[var(--text-disabled)]"
                        }`}
                      >
                        {available ? "Available" : "Taken"}
                      </span>
                      {r.PremiumRegistrationPrice && Number(r.PremiumRegistrationPrice) > 0 && (
                        <span className="mono text-sm text-[var(--text-secondary)]">
                          ${r.PremiumRegistrationPrice}
                        </span>
                      )}
                      {available && (
                        <Button
                          data-testid={`buy-${r.Domain}`}
                          size="sm"
                          onClick={() => navigate(`/buy?domain=${encodeURIComponent(r.Domain)}`)}
                          className="brand-bg hover:brand-bg text-white rounded-sm h-7 px-3 text-xs"
                        >
                          <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />
                          Buy
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Section>
    </div>
  );
};

export default DomainSearch;
